<script setup lang="ts">
/**
 * ThePublicFooter - подвал публичной зоны.
 * Логотип, описание, колонки ссылок, copyright.
 */
import { useI18n } from 'vue-i18n'
import BuildVersionBadge from '@/components/shared/BuildVersionBadge.vue'

const { t } = useI18n()

const currentYear = new Date().getFullYear()

interface FooterColumn {
  key: string
  titleKey: string
  links: { key: string; labelKey: string; path: string }[]
}

const columns: FooterColumn[] = [
  {
    key: 'product',
    titleKey: 'public.footer.columns.product',
    links: [
      { key: 'screener', labelKey: 'screener.title', path: '/screener' },
      { key: 'features', labelKey: 'public.footer.links.features', path: '/features' },
      { key: 'pricing', labelKey: 'public.footer.links.pricing', path: '/pricing' },
      { key: 'api', labelKey: 'public.footer.links.api', path: '/features' },
    ],
  },
  {
    key: 'company',
    titleKey: 'public.footer.columns.company',
    links: [
      { key: 'about', labelKey: 'public.footer.links.about', path: '/about' },
      { key: 'blog', labelKey: 'public.footer.links.blog', path: '/blog' },
      { key: 'careers', labelKey: 'public.footer.links.careers', path: '/about' },
    ],
  },
  {
    key: 'resources',
    titleKey: 'public.footer.columns.resources',
    links: [
      { key: 'docs', labelKey: 'public.footer.links.docs', path: '/blog' },
      { key: 'support', labelKey: 'public.footer.links.support', path: '/about' },
      { key: 'status', labelKey: 'public.footer.links.status', path: '/about' },
    ],
  },
  {
    key: 'legal',
    titleKey: 'public.footer.columns.legal',
    links: [
      { key: 'terms', labelKey: 'public.footer.links.terms', path: '/about' },
      { key: 'privacy', labelKey: 'public.footer.links.privacy', path: '/about' },
      { key: 'cookies', labelKey: 'public.footer.links.cookies', path: '/about' },
    ],
  },
]
</script>

<template>
  <footer class="border-t border-primary/5 bg-base-200/50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
      <!-- Основной контент -->
      <div class="py-12 lg:py-16 grid grid-cols-2 md:grid-cols-6 gap-8">
        <!-- Логотип и описание -->
        <div class="col-span-2">
          <RouterLink
            to="/"
            class="flex items-center gap-2 mb-4"
          >
            <img
              src="/logo_altermex.png"
              alt="AlterMex"
              class="w-8 h-8"
            >
            <span class="text-brand text-lg">AlterMex</span>
          </RouterLink>
          <p class="text-sm text-base-content/40 max-w-xs leading-relaxed">
            {{ t('public.footer.description') }}
          </p>
        </div>

        <!-- Колонки ссылок -->
        <div
          v-for="column in columns"
          :key="column.key"
        >
          <h3 class="text-sm font-semibold text-base-content/70 mb-4">
            {{ t(column.titleKey) }}
          </h3>
          <ul class="space-y-3">
            <li
              v-for="link in column.links"
              :key="link.key"
            >
              <RouterLink
                :to="link.path"
                class="text-sm text-base-content/40 hover:text-primary transition-colors"
              >
                {{ t(link.labelKey) }}
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <!-- Copyright -->
      <div class="py-6 border-t border-primary/5">
        <div class="flex flex-col items-center gap-1.5 text-center sm:items-start sm:text-left">
          <p class="text-xs text-base-content/30">
            {{ t('public.footer.copyright', { year: currentYear }) }}
          </p>
          <BuildVersionBadge />
        </div>
      </div>
    </div>
  </footer>
</template>
